import React from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { recordLinkClick } from '../lib/supabase';
import TipJar from './TipJar';
import NFTGallery from './NFTGallery';
import TokenGatedContent from './TokenGatedContent';
import { FiExternalLink, FiShoppingCart, FiCalendar } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const BlockRenderer = ({ block, theme, profile }) => {
  const { walletAddress } = useAuth();

  const handleLinkClick = async (url) => {
    if (profile?.id && block?.id) {
      await recordLinkClick(
        profile.id,
        block.id,
        block.type,
        url,
        walletAddress
      );
    }
  };

  const renderLinkBlock = () => (
    <a
      href={block.data.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => handleLinkClick(block.data.url)}
      className={`block w-full p-4 rounded-xl ${theme.card || theme.bg} border ${theme.border} ${theme.hover || 'hover:opacity-80'} transition-all duration-300`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {block.data.icon && (
            <div className={`w-10 h-10 rounded-lg ${theme.accent.replace('text-', 'bg-')} bg-opacity-10 flex items-center justify-center border ${theme.border}`}>
              <span className="text-lg">{block.data.icon}</span>
            </div>
          )}
          <div className="flex-1">
            <h3 className={`font-semibold ${theme.text}`}>
              {block.data.title}
            </h3>
            {block.data.description && (
              <p className={`text-sm ${theme.subtext} mt-1`}>
                {block.data.description}
              </p>
            )}
          </div>
        </div>
        <SafeIcon icon={FiExternalLink} className={`${theme.subtext} flex-shrink-0 ml-3`} />
      </div>
    </a>
  );

  const renderImageBlock = () => (
    <div className={`rounded-xl overflow-hidden border ${theme.border} ${theme.card || theme.bg} shadow-lg`}>
      <img src={block.data.url} alt={block.data.title || 'Image'} className="w-full h-auto object-cover" loading="lazy" />
      {(block.data.title || block.data.description) && (
        <div className={`p-4 ${theme.bg}`}>
          {block.data.title && (
            <h3 className={`font-semibold ${theme.text} mb-2`}>
              {block.data.title}
            </h3>
          )}
          {block.data.description && (
            <p className={`text-sm ${theme.subtext}`}>
              {block.data.description}
            </p>
          )}
        </div>
      )}
    </div>
  );

  const renderVideoBlock = () => {
    const getEmbedUrl = (url) => {
      // YouTube
      if (url.includes('youtube.com/watch')) {
        const videoId = url.split('v=')[1]?.split('&')[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
      if (url.includes('youtu.be/')) {
        const videoId = url.split('youtu.be/')[1]?.split('?')[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
      // TikTok (simplified)
      if (url.includes('tiktok.com')) {
        return url;
      }
      return url;
    };
    const embedUrl = getEmbedUrl(block.data.url);
    return (
      <div className={`rounded-xl overflow-hidden border ${theme.border} ${theme.bg}`}>
        {block.data.title && (
          <div className="p-4">
            <h3 className={`font-medium ${theme.text}`}>
              {block.data.title}
            </h3>
            {block.data.description && (
              <p className={`text-sm ${theme.subtext} mt-1`}>
                {block.data.description}
              </p>
            )}
          </div>
        )}
        <div className="aspect-video">
          <iframe
            src={embedUrl}
            title={block.data.title || 'Video'}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    );
  };

  const renderProductBlock = () => (
    <div className={`rounded-xl border ${theme.border} ${theme.card || theme.bg} overflow-hidden shadow-lg`}>
      {block.data.image && (
        <div className="aspect-square bg-gray-100 overflow-hidden">
          <img src={block.data.image} alt={block.data.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" loading="lazy" />
        </div>
      )}
      <div className="p-5">
        <h3 className={`font-semibold text-lg ${theme.text} mb-2`}>
          {block.data.title}
        </h3>
        {block.data.description && (
          <p className={`text-sm ${theme.subtext} mb-4 leading-relaxed`}>
            {block.data.description}
          </p>
        )}
        <div className="flex items-center justify-between">
          {block.data.price && (
            <span className={`text-2xl font-bold ${theme.accent}`}>
              {block.data.price}
            </span>
          )}
          <a
            href={block.data.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center px-5 py-2.5 rounded-lg ${theme.button || 'bg-indigo-600 hover:bg-indigo-700 text-white'} transition-all duration-300 text-sm font-semibold shadow-md hover:shadow-lg`}
          >
            <SafeIcon icon={FiShoppingCart} className="mr-2" />
            {block.data.cta || 'Buy Now'}
          </a>
        </div>
      </div>
    </div>
  );

  const renderCalendarBlock = () => (
    <a
      href={block.data.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => handleLinkClick(block.data.url)}
      className={`block w-full p-4 rounded-xl border ${theme.border} ${theme.bg} hover:opacity-80 transition-opacity`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-12 h-12 rounded-lg ${theme.accent} bg-opacity-20 flex items-center justify-center`}>
            <SafeIcon icon={FiCalendar} className={`${theme.accent} text-xl`} />
          </div>
          <div>
            <h3 className={`font-medium ${theme.text}`}>
              {block.data.title || 'Book a Meeting'}
            </h3>
            {block.data.description && (
              <p className={`text-sm ${theme.subtext} mt-1`}>
                {block.data.description}
              </p>
            )}
          </div>
        </div>
        <SafeIcon icon={FiExternalLink} className={`${theme.subtext} flex-shrink-0`} />
      </div>
    </a>
  );

  const renderTextBlock = () => (
    <div className={`p-5 rounded-xl ${theme.card || theme.bg} border ${theme.border} shadow-md`}>
      {block.data.title && (
        <h3 className={`font-semibold text-lg ${theme.text} mb-3`}>
          {block.data.title}
        </h3>
      )}
      <div className={`${theme.subtext} leading-relaxed`}>
        {block.data.content?.split('\n').map((line, index) => (
          <p key={index} className="mb-3 last:mb-0">
            {line}
          </p>
        ))}
      </div>
    </div>
  );

  const renderTipJarBlock = () => {
    if (!block.data.enabled) return null;
    return <TipJar profile={profile} theme={theme} />;
  };

  const renderNFTGalleryBlock = () => {
    if (!block.data.enabled) return null;
    return <NFTGallery profile={profile} theme={theme} />;
  };

  const renderTokenGatedBlock = () => {
    return <TokenGatedContent block={block} theme={theme} />;
  };

  const renderGalleryBlock = () => {
    const images = block.data?.images || [];
    const layout = block.data?.layout || 'grid';

    if (images.length === 0) {
      return (
        <div className={`p-8 rounded-xl border ${theme.border} ${theme.card || theme.bg} text-center`}>
          <p className={`${theme.subtext}`}>No images in gallery</p>
        </div>
      );
    }

    if (layout === 'carousel') {
      return (
        <div className={`rounded-xl overflow-hidden border ${theme.border} ${theme.card || theme.bg} shadow-lg`}>
          {block.data.title && (
            <div className={`p-4 border-b ${theme.border}`}>
              <h3 className={`font-semibold ${theme.text}`}>{block.data.title}</h3>
            </div>
          )}
          <div className="overflow-x-auto">
            <div className="flex space-x-4 p-4">
              {images.map((img, index) => (
                <div key={index} className="flex-shrink-0">
                  <img
                    src={img.url || img}
                    alt={img.alt || `Gallery image ${index + 1}`}
                    className="w-64 h-64 object-cover rounded-lg hover:scale-105 transition-transform duration-300 shadow-md"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Grid layout
    const cols = Math.min(images.length, 3);
    const gridCols = cols === 1 ? 'grid-cols-1' : cols === 2 ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-3';
    return (
      <div className={`rounded-xl overflow-hidden border ${theme.border} ${theme.card || theme.bg} shadow-lg`}>
        {block.data.title && (
          <div className={`p-4 border-b ${theme.border}`}>
            <h3 className={`font-semibold ${theme.text}`}>{block.data.title}</h3>
          </div>
        )}
        <div className={`grid ${gridCols} gap-3 p-4`}>
          {images.map((img, index) => (
            <div key={index} className="relative aspect-square overflow-hidden rounded-lg group cursor-pointer">
              <img
                src={img.url || img}
                alt={img.alt || `Gallery image ${index + 1}`}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render based on block type
  switch (block.type) {
    case 'link':
      return renderLinkBlock();
    case 'image':
      return renderImageBlock();
    case 'video':
      return renderVideoBlock();
    case 'product':
      return renderProductBlock();
    case 'calendar':
      return renderCalendarBlock();
    case 'gallery':
      return renderGalleryBlock();
    case 'text':
      return renderTextBlock();
    case 'tipjar':
      return renderTipJarBlock();
    case 'nftgallery':
      return renderNFTGalleryBlock();
    case 'tokengated':
      return renderTokenGatedBlock();
    default:
      return (
        <div className={`p-4 rounded-xl border ${theme.border} ${theme.bg}`}>
          <p className={`${theme.subtext} text-center`}>
            Unknown block type: {block.type}
          </p>
        </div>
      );
  }
};

export default BlockRenderer;